<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import DocumentRequestTemplatesEditor from '../tickets/categories/DocumentRequestTemplatesEditor.vue';
import StagesEditor from './StagesEditor.vue';
import FormsEditor from './FormsEditor.vue';
import PaymentTab from './PaymentTab.vue';
import RequirementsTab from './RequirementsTab.vue';

const props = defineProps({
  typeId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const loading = ref(true);
const saving = ref(false);
const activeTab = ref('general');

const form = reactive({
  name: '',
  code: '',
  category: '',
  description: '',
  active: true,
  default_sla_hours: null,
  payment_required: false,
  payment_currency: 'MXN',
  payment_config: {},
  config: {},
});

const TABS = [
  { key: 'general', label: 'TRAMITE_TYPES.EDITOR.TABS.GENERAL' },
  { key: 'forms', label: 'TRAMITE_TYPES.EDITOR.TABS.INTAKE' },
  { key: 'documents', label: 'TRAMITE_TYPES.EDITOR.TABS.DOCUMENTS' },
  { key: 'requirements', label: 'TRAMITE_TYPES.EDITOR.TABS.REQUIREMENTS' },
  { key: 'payment', label: 'TRAMITE_TYPES.EDITOR.TABS.PAYMENT' },
  { key: 'stages', label: 'TRAMITE_TYPES.EDITOR.TABS.STAGES' },
  { key: 'output', label: 'TRAMITE_TYPES.EDITOR.TABS.OUTPUT', disabled: true },
  {
    key: 'signature',
    label: 'TRAMITE_TYPES.EDITOR.TABS.SIGNATURE',
    disabled: true,
  },
];

// Required-document checklist lives in config.document_requests (same shape ticket subcategories use).
const documentRequests = computed({
  get: () =>
    Array.isArray(form.config?.document_requests)
      ? form.config.document_requests
      : [],
  set: value => {
    form.config = { ...(form.config || {}), document_requests: value };
  },
});

function applyType(type) {
  form.name = type.name ?? '';
  form.code = type.code ?? '';
  form.category = type.category ?? '';
  form.description = type.description ?? '';
  form.active = type.active ?? true;
  form.default_sla_hours = type.default_sla_hours ?? null;
  form.payment_required = type.payment_required ?? false;
  form.payment_currency = type.payment_currency ?? 'MXN';
  form.payment_config = type.payment_config ? { ...type.payment_config } : {};
  form.config = type.config ? { ...type.config } : {};
}

onMounted(async () => {
  try {
    const type = await store.dispatch('tramiteTypes/fetchTramiteType', props.typeId);
    applyType(type);
  } catch {
    useAlert(t('TRAMITE_TYPES.EDITOR.LOAD_ERROR'));
    goBack();
  } finally {
    loading.value = false;
  }
});

function goBack() {
  router.push({
    name: 'tramite_types_settings_list',
    params: { accountId: accountId.value },
  });
}

async function save() {
  saving.value = true;
  try {
    await store.dispatch('tramiteTypes/updateTramiteType', {
      id: props.typeId,
      name: form.name,
      code: form.code,
      category: form.category,
      description: form.description,
      active: form.active,
      default_sla_hours: form.default_sla_hours,
      payment_required: form.payment_required,
      payment_currency: form.payment_currency,
      payment_config: form.payment_config,
      config: form.config,
    });
    useAlert(t('TRAMITE_TYPES.UPDATE_SUCCESS'));
  } catch (error) {
    const message =
      error.response?.data?.error || t('TRAMITE_TYPES.SAVE_ERROR');
    useAlert(message);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Header -->
    <div
      class="flex justify-between items-center px-6 h-14 border-b shrink-0 border-n-weak"
    >
      <div class="flex gap-3 items-center min-w-0">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :label="t('TRAMITE_TYPES.EDITOR.BACK')"
          @click="goBack"
        />
        <h1 class="text-sm font-medium truncate text-n-slate-12">
          {{ form.name || t('TRAMITE_TYPES.EDITOR.UNTITLED') }}
        </h1>
      </div>
      <Button
        :is-loading="saving"
        icon="i-lucide-save"
        :label="t('TRAMITE_TYPES.EDITOR.SAVE')"
        @click="save"
      />
    </div>

    <div v-if="loading" class="flex flex-1 justify-center items-center">
      <Spinner />
    </div>

    <div v-else class="flex overflow-hidden flex-1">
      <!-- Tabs -->
      <nav
        class="flex flex-col gap-1 p-3 w-56 border-r shrink-0 border-n-weak"
      >
        <button
          v-for="tab in TABS"
          :key="tab.key"
          type="button"
          :disabled="tab.disabled"
          class="px-3 py-2 text-sm text-left rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          :class="
            activeTab === tab.key
              ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="!tab.disabled && (activeTab = tab.key)"
        >
          {{ t(tab.label) }}
        </button>
      </nav>

      <!-- Tab body -->
      <div class="overflow-auto flex-1 p-6">
        <!-- General -->
        <div v-if="activeTab === 'general'" class="flex flex-col gap-4 max-w-xl">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TRAMITE_TYPES.GENERAL.NAME') }}
            </label>
            <input
              v-model="form.name"
              type="text"
              maxlength="255"
              class="px-3 py-2 w-full rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>
          <div class="flex gap-4">
            <div class="flex flex-col flex-1 gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('TRAMITE_TYPES.GENERAL.CODE') }}
              </label>
              <input
                v-model="form.code"
                type="text"
                maxlength="5"
                class="px-3 py-2 w-full font-mono uppercase rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
              <span class="text-xs text-n-slate-10">
                {{ t('TRAMITE_TYPES.GENERAL.CODE_HELP') }}
              </span>
            </div>
            <div class="flex flex-col flex-1 gap-1">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('TRAMITE_TYPES.GENERAL.CATEGORY') }}
              </label>
              <input
                v-model="form.category"
                type="text"
                maxlength="255"
                class="px-3 py-2 w-full rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TRAMITE_TYPES.GENERAL.DESCRIPTION') }}
            </label>
            <textarea
              v-model="form.description"
              rows="3"
              class="px-3 py-2 w-full rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </div>
          <div class="flex flex-col gap-1 max-w-xs">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TRAMITE_TYPES.GENERAL.DEFAULT_SLA') }}
            </label>
            <input
              v-model.number="form.default_sla_hours"
              type="number"
              min="0"
              class="px-3 py-2 w-full rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
            <span class="text-xs text-n-slate-10">
              {{ t('TRAMITE_TYPES.GENERAL.DEFAULT_SLA_HELP') }}
            </span>
          </div>
          <label class="flex gap-2 items-center cursor-pointer">
            <input v-model="form.active" type="checkbox" class="rounded" />
            <span class="text-sm text-n-slate-12">
              {{ t('TRAMITE_TYPES.GENERAL.ACTIVE') }}
            </span>
          </label>
        </div>

        <!-- Formularios -->
        <FormsEditor v-else-if="activeTab === 'forms'" :type-id="props.typeId" />

        <!-- Required documents -->
        <div v-else-if="activeTab === 'documents'" class="flex flex-col gap-3 max-w-2xl">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('TRAMITE_TYPES.DOCUMENTS.TITLE') }}
          </h2>
          <p class="text-xs text-n-slate-10">
            {{ t('TRAMITE_TYPES.DOCUMENTS.HELP') }}
          </p>
          <DocumentRequestTemplatesEditor v-model="documentRequests" />
        </div>

        <!-- Requirements (requisitos) -->
        <RequirementsTab
          v-else-if="activeTab === 'requirements'"
          :config="form.config"
          @update:config="form.config = $event"
        />

        <!-- Payment -->
        <PaymentTab
          v-else-if="activeTab === 'payment'"
          v-model:required="form.payment_required"
          v-model:currency="form.payment_currency"
          v-model:config="form.payment_config"
        />

        <!-- Stages & SLA -->
        <StagesEditor
          v-else-if="activeTab === 'stages'"
          :type-id="props.typeId"
        />

        <!-- Output / Signature (coming soon) -->
        <div v-else class="py-12 text-sm text-center text-n-slate-10">
          {{ t('TRAMITE_TYPES.EDITOR.COMING_SOON') }}
        </div>
      </div>
    </div>
  </div>
</template>
